import React, {useEffect, useState} from 'react';
import {getConfig} from "../support/Config";

// Вариант изображения: облегчённые форматы запрашиваются с бэкенда через ?variant=.
export type ImageVariant = 'thumbnail' | 'medium' | 'original';

/**
 * Открывает оригинал изображения в новой вкладке. Эндпоинт изображений принимает
 * cookie-сессию (как и сам AuthImage через credentials:'include'), поэтому прямая
 * same-origin навигация авторизуется и отдаёт картинку inline.
 */
export function openImageInNewWindow(imageKey: string, basePath = '/clothing/images/'): void {
    if (!imageKey) return;
    const base = getConfig('apiBaseUrl');
    window.open(`${base}${basePath}${imageKey}`, '_blank', 'noopener');
}

interface AuthImageProps {
    imageKey: string;
    alt: string;
    className?: string;
    // API path prefix that serves the image by key. Defaults to the clothing images endpoint.
    basePath?: string;
    // thumbnail — мелкие изображения (таблица/сетка), medium — карточка вещи, original — полный просмотр.
    variant?: ImageVariant;
    // По клику открывает оригинал в новой вкладке (курсор-zoom). Не задавать, если у родителя свой onClick.
    expandable?: boolean;
}

const AuthImage: React.FC<AuthImageProps> = ({imageKey, alt, className = '', basePath = '/clothing/images/', variant = 'original', expandable = false}) => {
    const [src, setSrc] = useState<string | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!imageKey || error) return;
        let revoked = false;
        (async () => {
            try {
                const base = getConfig('apiBaseUrl');
                const variantQuery = variant && variant !== 'original' ? `?variant=${variant}` : '';
                const url = `${base}${basePath}${imageKey}${variantQuery}`;
                const res = await fetch(url, {
                    credentials: 'include'
                });
                if (!res.ok) {
                    setError(true);
                    return;
                }
                const blob = await res.blob();
                if (revoked) return;
                const objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            } catch {
                setError(true);
            }
        })();
        return () => {
            revoked = true;
            setSrc(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
        };
    }, [imageKey, error, basePath, variant]);

    if (error || !src) {
        return (
            <span data-testid="auth-image"
                  className={`flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 ${className}`}>
                👕
            </span>
        );
    }

    if (expandable) {
        return (
            <img
                src={src} alt={alt}
                data-testid="auth-image"
                className={`${className} cursor-zoom-in`}
                title="Открыть оригинал в новой вкладке"
                onClick={(e) => {
                    e.stopPropagation();
                    openImageInNewWindow(imageKey, basePath);
                }}
            />
        );
    }

    return <img src={src} alt={alt} data-testid="auth-image" className={className}/>;
};

export default AuthImage;
